import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useMemo } from "react";

export interface WatchlistItem {
  id: string;
  user_id: string;
  auction_key: string;
  added_at: string;
  notes: string | null;
}

// OPTIMIERT: Haupthook mit Cache für schnelle Lookups
export const useWatchlist = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['watchlist', userId],
    queryFn: async (): Promise<WatchlistItem[]> => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('auction_watchlist')
        .select('*')
        .eq('user_id', userId)
        .order('added_at', { ascending: false });

      if (error) {
        console.error('Error fetching watchlist:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!userId,
    staleTime: 1000 * 30,
  });
};

// OPTIMIERT: Nutzt gecachte Watchlist statt separater Query
export const useIsInWatchlist = (userId: string | undefined, auctionKey: string | undefined) => {
  const { data: watchlist } = useWatchlist(userId);
  
  return useMemo(() => {
    if (!userId || !auctionKey || !watchlist) return false;
    return watchlist.some(item => item.auction_key === auctionKey);
  }, [watchlist, userId, auctionKey]);
};

// OPTIMIERT: Hook mit reaktiver Prüfung
export const useIsInWatchlistQuery = (userId: string | undefined, auctionKey: string | undefined) => {
  const { data: watchlist, isLoading } = useWatchlist(userId);
  
  const isInWatchlist = useMemo(() => {
    if (!userId || !auctionKey || !watchlist) return false;
    return watchlist.some(item => item.auction_key === auctionKey);
  }, [watchlist, userId, auctionKey]);

  return { data: isInWatchlist, isLoading };
};

// Add to watchlist with optimistic update
export const useAddToWatchlist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ auctionKey, notes }: { auctionKey: string; notes?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('auction_watchlist')
        .insert({
          user_id: user.id,
          auction_key: auctionKey,
          notes: notes || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Nur Watchlist invalidieren - keine separaten Queries mehr
      queryClient.invalidateQueries({ queryKey: ['watchlist'] });
      toast.success('Zur Watchlist hinzugefügt');
    },
    onError: (error: Error) => {
      if (error.message.includes('duplicate')) {
        toast.info('Bereits in der Watchlist');
      } else {
        toast.error('Fehler beim Hinzufügen');
      }
    },
  });
};

// Remove from watchlist with optimistic update
export const useRemoveFromWatchlist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (auctionKey: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('auction_watchlist')
        .delete()
        .eq('user_id', user.id)
        .eq('auction_key', auctionKey);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['watchlist'] });
      toast.success('Von Watchlist entfernt');
    },
    onError: () => {
      toast.error('Fehler beim Entfernen');
    },
  });
};

// Toggle watchlist
export const useToggleWatchlist = () => {
  const addMutation = useAddToWatchlist();
  const removeMutation = useRemoveFromWatchlist();

  return {
    toggle: async (auctionKey: string, isInWatchlist: boolean) => {
      if (isInWatchlist) {
        await removeMutation.mutateAsync(auctionKey);
      } else {
        await addMutation.mutateAsync({ auctionKey });
      }
    },
    isPending: addMutation.isPending || removeMutation.isPending,
  };
};
